import React, { useEffect, useState } from 'react';
import { Plus, Save, Coins, Wallet, Gift, Scissors } from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../context/LanguageContext';

// Cash-out types are listed first - they are what an owner records most often
export const TRANSACTION_TYPES = [
  {
    value: 'advance',
    label: 'Advance',
    icon: Coins,
    hint: 'Cash given before salary is due',
    accent: 'orange'
  },
  {
    value: 'salary',
    label: 'Salary Paid',
    icon: Wallet,
    hint: 'A salary payment',
    accent: 'green'
  },
  {
    value: 'bonus',
    label: 'Bonus',
    icon: Gift,
    hint: 'Extra earnings on top of salary',
    accent: 'blue'
  },
  {
    value: 'deduction',
    label: 'Deduction',
    icon: Scissors,
    hint: 'Fine, damage or unpaid leave',
    accent: 'red'
  }
];

const accentClasses = {
  orange: 'border-orange-500 bg-orange-50 text-orange-700',
  green: 'border-green-500 bg-green-50 text-green-700',
  blue: 'border-blue-500 bg-blue-50 text-blue-700',
  red: 'border-red-500 bg-red-50 text-red-700'
};

function toDateInput(value) {
  return value ? new Date(value).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
}

function StaffTransactionModal({ isOpen, onClose, onSubmit, staff, transaction = null }) {
  const { t } = useLanguage();
  const isEdit = Boolean(transaction);
  const [formData, setFormData] = useState({
    type: 'advance',
    amount: '',
    date: toDateInput(),
    note: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(
      transaction
        ? {
            type: transaction.type,
            amount: transaction.amount ?? '',
            date: toDateInput(transaction.date),
            note: transaction.note || ''
          }
        : { type: 'advance', amount: '', date: toDateInput(), note: '' }
    );
  }, [isOpen, transaction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await onSubmit({ ...formData, amount: parseFloat(formData.amount) || 0 });
    } finally {
      setSubmitting(false);
    }
  };

  const balance = staff?.ledger?.balance ?? 0;
  const selected = TRANSACTION_TYPES.find((t) => t.value === formData.type);
  const isPayout = formData.type === 'advance' || formData.type === 'salary';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Entry' : `Record Entry${staff ? ` - ${staff.name}` : ''}`}
      size="lg"
    >
      <div className="p-4 sm:p-6">
        {!isEdit && staff && (
          <div className="mb-5 p-4 rounded-xl bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {balance >= 0 ? 'Currently owed to them' : 'Paid ahead by'}
              </span>
              <span className={`text-lg font-bold ${balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                Rs. {Math.abs(balance).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Entry Type')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {TRANSACTION_TYPES.map(({ value, label, icon: Icon, accent }) => {
                const isSelected = formData.type === value;

                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: value })}
                    className={`flex items-center justify-center gap-2 px-3 py-3 min-h-[48px] rounded-xl border-2 text-sm font-medium transition-colors ${
                      isSelected
                        ? accentClasses[accent]
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
            {selected && <p className="text-xs text-gray-500 mt-2">{selected.hint}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Amount (Rs.)')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">{t('Rs')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="1"
                  className="w-full pl-8 pr-3 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Date')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {!isEdit && balance > 0 && (
            <div className="bg-gray-50 p-4 rounded-xl">
              <label className="block text-sm font-medium text-gray-700 mb-3">{t('Quick Amount')}</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, amount: balance.toFixed(2) })}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  Settle full Rs {balance.toFixed(2)}
                </button>
                {[500, 1000, 2000, 5000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    Rs {amount}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Note')}</label>
            <input
              type="text"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder={t('e.g., Dashain advance, broke two plates')}
              className="w-full px-3 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {isPayout && (
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
              {t('This is cash leaving the till, so it is also recorded under Expenses automatically.')}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-5 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 sm:flex-none sm:min-w-[150px] px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg disabled:opacity-60"
            >
              {isEdit ? <Save className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              <span>{isEdit ? 'Save Changes' : 'Record Entry'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none sm:min-w-[100px] px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
            >
              {t('Cancel')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default StaffTransactionModal;
